/**
 * SettingsPage - Account & Subscription Settings
 * 
 * Scout by EQ.app branding applied.
 * 
 * Tabs:
 * - Account: Profile, usage stats
 * - Subscription: Stripe pricing table
 * - Notifications: Email/push preferences
 * - Integrations: CRM connections (coming soon)
 * 
 * Brand Guidelines:
 * - Primary: #5a20ff (purple)
 * - Secondary: #52dcb3 (teal)
 * - Background: #f8f8f6 (off-white)
 * - Typography: serif headings
 */

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Settings,
  User,
  Users,
  UserPlus,
  Bell,
  CreditCard,
  LinkIcon,
  Gauge,
  Clock,
  Sparkles,
  ArrowLeft,
  Zap,
  Gift,
  Check,
  LogOut,
  MessageSquare,
  Mail,
  Shield,
  Crown,
  CheckCircle,
  AlertCircle,
  Loader2,
  Trash2,
  Copy,
  ExternalLink,
  Pencil,
  Save,
  X,
  Send,
} from "lucide-react";
import BrandVoiceSettings from "../scout/BrandVoiceSettings";
// OutreachHistory removed — outreach is now managed in Reach
import api from "../../utils/axios";
import { useAuth } from "../../contexts/AuthContext";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { cn } from "../../lib/utils";
import {
  startCheckout,
  startCreditPurchase,
  getOrgMembers,
  getOrgInvitations,
  inviteMember,
  removeMember as removeOrgMember,
  revokeInvitation as revokeOrgInvitation,
  resendInvitation as resendOrgInvitation,
  updateMemberRole as updateOrgMemberRole,
  getOrgUsage,
  updateOrgSettings,
} from "../../services/subscriptionService";

// EQ.app Brand Constants
const BRAND = {
  purple: "#5a20ff",
  teal: "#52dcb3",
  bgOffWhite: "#f8f8f6",
};

// Dynamic Stripe script loader
const loadStripeScript = () => {
  return new Promise((resolve, reject) => {
    if (
      document.querySelector('script[src*="stripe.com/v3/pricing-table.js"]')
    ) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/pricing-table.js";
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const Tabs = {
  ACCOUNT: "Account",
  BRAND_VOICE: "Brand Voice",
  WORKSPACE: "Workspace",
  NOTIFICATIONS: "Notifications",
  INTEGRATIONS: "Integrations",
};

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { subscription: realSubscription, planInfo } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();

  // Check for tab parameter in URL and set initial tab
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get("tab");

    // Map URL tab parameters to actual tab values
    switch (tabParam) {
      case "brand-voice":
        return Tabs.BRAND_VOICE;
      case "outreach":
        return Tabs.BRAND_VOICE;
      case "subscription":
      case "team":
      case "org":
      case "workspace":
        return Tabs.WORKSPACE;
      case "notifications":
        return Tabs.NOTIFICATIONS;
      case "integrations":
        return Tabs.INTEGRATIONS;
      case "account":
      default:
        return Tabs.ACCOUNT;
    }
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());

  // Profile from backend (no mock defaults)
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  // Use ONLY real subscription data from context - no fallbacks
  const subscription = realSubscription
    ? {
        plan:
          planInfo?.name ||
          realSubscription.status?.charAt(0).toUpperCase() +
            realSubscription.status?.slice(1),
        billingCycle: "Monthly",
        nextBillingDate: null, // Could be enhanced with Stripe data
        status: realSubscription.status,
        priceId: realSubscription.stripePriceId,
        dailyLimit: realSubscription.dailyLimit,
        usedToday: realSubscription.usedToday,
        remaining: realSubscription.remaining,
        unlimited: !!realSubscription.unlimited,
      }
    : null;
  const [plans, setPlans] = useState([]);
  // Use ONLY real usage data from subscription context - no fallbacks
  const usage = useMemo(() => {
    return realSubscription
      ? {
          used: realSubscription.usedToday || 0,
          limit: realSubscription.dailyLimit || 0,
          remaining: realSubscription.remaining || 0,
          status:
            realSubscription.remaining > 0
              ? "OK"
              : realSubscription.remaining === 0
              ? "WARNING"
              : "EXCEEDED",
          date: new Date().toLocaleDateString(),
        }
      : null;
  }, [realSubscription]);

  const usagePct = useMemo(() => {
    if (!usage || !usage.limit) return 0;
    return Math.min(100, Math.round((usage.used / usage.limit) * 100));
  }, [usage]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const [p, pl] = await Promise.all([
          api
            .get("/api/settings/profile")
            .catch(() => ({ data: { profile: null } })),
          api.get("/api/settings/plans").catch(() => ({ data: { plans: [] } })),
        ]);
        if (!mounted) return;
        if (p?.data?.profile) setProfile(p.data.profile);
        if (pl?.data?.plans) setPlans(pl.data.plans);
        // Note: subscription and usage data now comes from SubscriptionContext
      } catch (e) {
        // Keep defaults on error
        // eslint-disable-next-line no-console
        console.warn("Settings stubs not available; using defaults.");
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Update active tab when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get("tab");

    // Map URL tab parameters to actual tab values
    switch (tabParam) {
      case "brand-voice":
        setActiveTab(Tabs.BRAND_VOICE);
        break;
      case "outreach":
        setActiveTab(Tabs.BRAND_VOICE);
        break;
      case "subscription":
      case "team":
      case "org":
      case "workspace":
        setActiveTab(Tabs.WORKSPACE);
        break;
      case "notifications":
        setActiveTab(Tabs.NOTIFICATIONS);
        break;
      case "integrations":
        setActiveTab(Tabs.INTEGRATIONS);
        break;
      case "learn-more":
        navigate("/dashboard/learn");
        break;
      case "account":
      default:
        setActiveTab(Tabs.ACCOUNT);
        break;
    }
  }, [location.search]);

  const [showBrandVoiceModal, setShowBrandVoiceModal] = useState(false);

  const renderTab = () => {
    switch (activeTab) {
      case Tabs.ACCOUNT:
        return (
          <AccountTab profile={profile} setProfile={setProfile} user={user} />
        );
      case Tabs.BRAND_VOICE:
        return (
          <BrandVoiceTab onOpenModal={() => setShowBrandVoiceModal(true)} />
        );
      case Tabs.WORKSPACE:
        return <WorkspaceTab />;
      case Tabs.NOTIFICATIONS:
        return <ComingSoonTab feature="Notifications" />;
      case Tabs.INTEGRATIONS:
        return <IntegrationsTab />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f8f6]">
      {/* Premium page enter animation */}
      <style>{`
        @keyframes eq-page-enter {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes eq-tab-fade {
          from { opacity: 0; transform: translateX(8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      
      {/* Full page wrapper with Scout brand styling */}
      <div 
        className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-6xl mx-auto my-8 min-h-[600px] lg:min-h-[700px] flex flex-col lg:flex-row overflow-hidden border border-black/5"
        style={{ animation: 'eq-page-enter 700ms cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Full width container */}
        <div className="flex flex-col w-full">
          {/* Header - Premium glass effect */}
          <div className="flex items-center justify-between p-4 lg:p-6 border-b border-black/5 bg-gradient-to-r from-[#5a20ff]/5 to-[#52dcb3]/5">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/app")}
                className="w-8 h-8 lg:w-10 lg:h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95 shadow-sm"
                title="Go back"
              >
                <ArrowLeft className="w-4 h-4 lg:w-5 lg:h-5 text-[#5a20ff]" />
              </button>
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-xl flex items-center justify-center shadow-lg shadow-[#5a20ff]/25">
                <Settings className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
              </div>
              <div>
                <h2 
                  className="text-lg lg:text-xl font-bold text-black"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Settings
                </h2>
                <p className="text-xs lg:text-sm text-black/50">
                  Manage your account
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
            {/* Sidebar Navigation - Scout styled */}
            <div className="w-full lg:w-72 bg-[#f8f8f6]/50 p-4 lg:p-6 border-b lg:border-b-0 lg:border-r border-black/5 flex flex-col">
              <nav className="flex flex-wrap lg:flex-col gap-2 lg:space-y-2 flex-1">
                {[
                  { key: Tabs.ACCOUNT, label: "Account", Icon: User },
                  {
                    key: Tabs.BRAND_VOICE,
                    label: "Brand Voice",
                    Icon: MessageSquare,
                  },
                  {
                    key: Tabs.WORKSPACE,
                    label: "Workspace",
                    Icon: Users,
                  },
                  {
                    key: Tabs.NOTIFICATIONS,
                    label: "Notifications",
                    Icon: Bell,
                  },
                  {
                    key: Tabs.INTEGRATIONS,
                    label: "Integrations",
                    Icon: LinkIcon,
                  },
                ].map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={cn(
                      "flex-1 lg:w-full justify-center lg:justify-start h-10 lg:h-12 font-semibold rounded-xl flex items-center gap-2 lg:gap-3 px-2 lg:px-4 py-2 text-xs sm:text-sm lg:text-base transition-all duration-200",
                      activeTab === key
                        ? "bg-white text-black shadow-lg border-l-4 border-[#5a20ff] scale-[1.02]"
                        : "text-black/50 hover:bg-white/80 hover:text-black hover:scale-[1.01]"
                    )}
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    <Icon className={cn("w-4 h-4 lg:w-5 lg:h-5 transition-colors duration-200", activeTab === key ? "text-[#5a20ff]" : "")} />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                ))}
              </nav>
              
              {/* Sign Out Button */}
              <div className="mt-4 pt-4 border-t border-black/10">
                <button
                  onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors font-serif"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 p-4 lg:p-8 overflow-y-auto bg-white">{renderTab()}</div>
          </div>
        </div>
      </div>

      {/* Brand Voice Settings Modal */}
      <BrandVoiceSettings 
        isOpen={showBrandVoiceModal} 
        onClose={() => setShowBrandVoiceModal(false)} 
      />

    </div>
  );
}

function ComingSoonTab({ feature }) {
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center px-8">
      <div className="mb-6">
        <div className="w-20 h-20 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-xl shadow-[#5a20ff]/20">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
      </div>

      <h3 className="text-2xl font-semibold text-black mb-3 font-serif">
        {feature} Coming Soon
      </h3>

      <p className="text-black/60 text-lg mb-6 max-w-md">
        We're working hard to bring you {feature.toLowerCase()} features. Stay
        tuned for updates!
      </p>

      <div className="flex items-center gap-2 text-sm text-[#5a20ff] bg-[#5a20ff]/10 px-4 py-2 rounded-full font-medium font-serif">
        <Clock className="w-4 h-4" />
        <span>In Development</span>
      </div>

      <div className="mt-8 text-sm text-black/40">
        <p>Want to be notified when this feature launches?</p>
        <p className="mt-1">Contact us and we'll keep you in the loop!</p>
      </div>
    </div>
  );
}

function BrandVoiceTab({ onOpenModal }) {
  const { org, userRole, isTeamPlan } = useSubscription();
  const isOwnerOrAdmin = userRole === "owner" || userRole === "admin";
  const [orgVoice, setOrgVoice] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [showOrgEditor, setShowOrgEditor] = useState(false);

  useEffect(() => {
    if (isTeamPlan) {
      setLoadingOrg(true);
      api.get("/api/org/brand-voice")
        .then(res => setOrgVoice(res.data?.brandVoice || null))
        .catch(() => {})
        .finally(() => setLoadingOrg(false));
    }
  }, [isTeamPlan]);

  const hasOrgVoice = orgVoice && typeof orgVoice === 'object' && Object.keys(orgVoice).length > 0;

  return (
    <div className="max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-xl flex items-center justify-center shadow-lg shadow-[#5a20ff]/20">
          <MessageSquare className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-black font-serif">
            Brand Voice
          </h3>
          <p className="text-sm text-black/50">
            Configure how AI writes outreach messages on your behalf
          </p>
        </div>
      </div>

      {/* Team Brand Voice (owners/admins on team plans) */}
      {isTeamPlan && (
        <div className="bg-gradient-to-br from-[#5a20ff]/5 to-[#52dcb3]/5 rounded-2xl p-6 border border-[#5a20ff]/10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#5a20ff]" />
              <h4 className="text-base font-semibold text-black font-serif">Team Brand Voice</h4>
            </div>
            {hasOrgVoice && (
              <span className="text-xs text-[#52dcb3] font-medium bg-[#52dcb3]/10 px-2 py-0.5 rounded-full">Active</span>
            )}
          </div>
          {loadingOrg ? (
            <div className="flex items-center gap-2 text-sm text-black/40">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </div>
          ) : hasOrgVoice ? (
            <div className="space-y-2">
              <p className="text-sm text-black/60">
                Team default: <span className="font-medium text-black">{orgVoice.name || orgVoice.company_name || 'Custom voice'}</span>
                {orgVoice.tone && <span className="text-black/40"> · {orgVoice.tone}</span>}
              </p>
              <p className="text-xs text-black/40">
                All team members use this voice unless they set their own.
              </p>
              {isOwnerOrAdmin && (
                <button
                  onClick={() => setShowOrgEditor(true)}
                  className="mt-2 text-xs text-[#5a20ff] hover:text-[#4a18d9] font-medium transition-colors"
                >
                  Edit team voice
                </button>
              )}
            </div>
          ) : (
            <div>
              <p className="text-sm text-black/50 mb-3">
                No team brand voice set. {isOwnerOrAdmin ? "Set one so all members share a consistent outreach style." : "Ask an admin to configure one."}
              </p>
              {isOwnerOrAdmin && (
                <button
                  onClick={() => setShowOrgEditor(true)}
                  className="px-4 py-2 bg-[#5a20ff] text-white rounded-xl font-semibold hover:bg-[#4a18d9] transition-all text-xs flex items-center gap-2"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Set Team Voice
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Personal Brand Voice */}
      <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <User className="w-4 h-4 text-[#5a20ff]" />
          <h4 className="text-base font-semibold text-black font-serif">My Brand Voice</h4>
        </div>
        <p className="text-sm text-black/60 mb-4">
          {isTeamPlan
            ? "Override the team voice with your own personal style. If not set, the team voice is used."
            : "Set up your brand voice to ensure all AI-generated messages match your unique style and tone."}
        </p>
        <button
          onClick={onOpenModal}
          className="px-5 py-2.5 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#5a20ff]/30 transition-all hover:scale-[1.02] font-serif text-sm flex items-center gap-2"
        >
          <Sparkles className="w-4 h-4" />
          Configure My Voice
        </button>
      </div>

      {/* Org Brand Voice Editor Modal */}
      {showOrgEditor && (
        <OrgBrandVoiceEditor
          initial={orgVoice || {}}
          onClose={() => setShowOrgEditor(false)}
          onSaved={(updated) => { setOrgVoice(updated); setShowOrgEditor(false); }}
        />
      )}
    </div>
  );
}

function OrgBrandVoiceEditor({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial.name || '');
  const [tone, setTone] = useState(initial.tone || 'professional');
  const [companyName, setCompanyName] = useState(initial.company_name || '');
  const [signature, setSignature] = useState(initial.signature || '');
  const [customInstructions, setCustomInstructions] = useState(initial.custom_instructions || '');
  const [valueProps, setValueProps] = useState(initial.value_props?.length ? initial.value_props : ['']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const TONES = [
    { id: 'conversational', label: 'Conversational' },
    { id: 'professional', label: 'Professional' },
    { id: 'technical', label: 'Technical' },
    { id: 'casual', label: 'Casual' },
    { id: 'authoritative', label: 'Authoritative' },
  ];

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const brandVoice = {
        name: name.trim(),
        tone,
        company_name: companyName.trim(),
        signature: signature.trim(),
        custom_instructions: customInstructions.trim(),
        value_props: valueProps.filter(v => v.trim()),
      };
      let res;
      try {
        res = await api.put("/api/org/brand-voice", { brandVoice });
      } catch (firstErr) {
        // One silent retry handles transient org linkage race conditions.
        if (firstErr?.response?.status === 404) {
          await new Promise((resolve) => setTimeout(resolve, 250));
          res = await api.put("/api/org/brand-voice", { brandVoice });
        } else {
          throw firstErr;
        }
      }
      if (res.data?.success) {
        onSaved(res.data.brandVoice || brandVoice);
      } else {
        throw new Error(res.data?.error || 'Failed to save');
      }
    } catch (err) {
      setError('Could not save team voice right now. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg max-h-[85vh] bg-white rounded-2xl shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/5">
          <div>
            <h3 className="text-lg font-semibold text-black font-serif">Team Brand Voice</h3>
            <p className="text-xs text-black/40">Shared across all team members by default</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-full transition-colors">
            <X className="w-5 h-5 text-black/40" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-black/70 mb-1 block">Voice Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Write Group Outreach" className="w-full px-3 py-2 border border-black/10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff]" />
          </div>
          <div>
            <label className="text-sm font-medium text-black/70 mb-1 block">Company Name</label>
            <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Your company name" className="w-full px-3 py-2 border border-black/10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff]" />
          </div>
          <div>
            <label className="text-sm font-medium text-black/70 mb-2 block">Tone</label>
            <div className="flex flex-wrap gap-2">
              {TONES.map(t => (
                <button key={t.id} onClick={() => setTone(t.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all ${tone === t.id ? 'border-[#5a20ff] bg-[#5a20ff]/5 text-[#5a20ff]' : 'border-black/10 text-black/50 hover:border-black/20'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-black/70 mb-1 block">Signature</label>
            <textarea value={signature} onChange={e => setSignature(e.target.value)} rows={2} placeholder="Best regards,\nJohn from Write Group" className="w-full px-3 py-2 border border-black/10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff] resize-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-black/70 mb-1 block">Custom Instructions</label>
            <textarea value={customInstructions} onChange={e => setCustomInstructions(e.target.value)} rows={3} placeholder="Any specific instructions for the AI..." className="w-full px-3 py-2 border border-black/10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff] resize-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-black/70 mb-1 block">Value Propositions</label>
            {valueProps.map((vp, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={vp} onChange={e => { const n = [...valueProps]; n[i] = e.target.value; setValueProps(n); }} placeholder="Key value prop" className="flex-1 px-3 py-2 border border-black/10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff]" />
                {valueProps.length > 1 && (
                  <button onClick={() => setValueProps(valueProps.filter((_, j) => j !== i))} className="p-2 text-red-400 hover:text-red-600 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button onClick={() => setValueProps([...valueProps, ''])} className="text-xs text-[#5a20ff] hover:text-[#4a18d9] font-medium">+ Add value prop</button>
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-black/5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-black/50 hover:bg-black/5 rounded-lg transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-[#5a20ff] text-white rounded-lg font-semibold text-sm hover:bg-[#4a18d9] disabled:opacity-50 transition-colors flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Team Voice
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailProviderCard({ provider, label, iconSrc, description, connection, emailStats, onConnect, onDisconnect, isConnecting, isDisconnecting }) {
  return (
    <div className="bg-white rounded-2xl border border-black/5 overflow-hidden mb-6">
      <div className="p-6 border-b border-black/5 bg-gradient-to-r from-[#5a20ff]/5 to-[#52dcb3]/5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm border border-black/5">
            {iconSrc ? (
              <img src={iconSrc} alt={label} className="w-6 h-6" />
            ) : (
              <Mail className="w-6 h-6 text-[#5a20ff]" />
            )}
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-black font-serif">{label}</h4>
            <p className="text-sm text-black/50">{description}</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {connection ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <p className="font-semibold text-green-800">Connected</p>
                <p className="text-sm text-green-600">{connection.email}</p>
              </div>
              <button
                onClick={onDisconnect}
                disabled={isDisconnecting}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                title={`Disconnect ${label}`}
              >
                {isDisconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </div>

            {emailStats && (
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-[#f8f8f6] rounded-xl text-center">
                  <p className="text-2xl font-bold text-[#5a20ff]">{emailStats.totalSent}</p>
                  <p className="text-xs text-black/50 mt-1">Emails Sent</p>
                </div>
                <div className="p-4 bg-[#f8f8f6] rounded-xl text-center">
                  <p className="text-2xl font-bold text-[#52dcb3]">{emailStats.emailsOpened}</p>
                  <p className="text-xs text-black/50 mt-1">Opened</p>
                </div>
                <div className="p-4 bg-[#f8f8f6] rounded-xl text-center">
                  <p className="text-2xl font-bold text-black">{emailStats.openRate}%</p>
                  <p className="text-xs text-black/50 mt-1">Open Rate</p>
                </div>
              </div>
            )}

            <p className="text-sm text-black/50">
              Emails sent through EQ will appear in your {label} Sent folder. Open tracking is enabled.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <p className="font-semibold text-blue-800">Not Connected</p>
                <p className="text-sm text-blue-600">Connect {label} to send emails directly from EQ</p>
              </div>
            </div>

            <div className="space-y-3">
              <h5 className="font-semibold text-black text-sm">Why connect {label}?</h5>
              <ul className="space-y-2 text-sm text-black/60">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#52dcb3]" />Send emails from your own address</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#52dcb3]" />Emails appear in your Sent folder</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#52dcb3]" />Track who opens your emails</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#52dcb3]" />No need to copy/paste</li>
              </ul>
            </div>

            <button
              onClick={onConnect}
              disabled={isConnecting}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#5a20ff]/30 transition-all hover:scale-[1.02] font-serif flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnecting ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Connecting...</>
              ) : (
                <><Mail className="w-4 h-4" />Connect {label}</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function IntegrationsTab() {
  const [gmailConnection, setGmailConnection] = useState(null);
  const [outlookConnection, setOutlookConnection] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnectingGmail, setIsDisconnectingGmail] = useState(false);
  const [isDisconnectingOutlook, setIsDisconnectingOutlook] = useState(false);
  const [emailStats, setEmailStats] = useState(null);
  const [isConnectingGmail, setIsConnectingGmail] = useState(false);
  const [isConnectingOutlook, setIsConnectingOutlook] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('email_connected') === 'gmail' || params.get('email_connected') === 'outlook') {
      fetchEmailStatus();
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    }
  }, []);

  const fetchEmailStatus = async () => {
    setIsLoading(true);
    try {
      const [statusRes, statsRes] = await Promise.all([
        api.get('/api/email/status'),
        api.get('/api/email/stats').catch(() => ({ data: { stats: null } })),
      ]);

      const data = statusRes.data || {};
      setGmailConnection(data.gmail?.connected ? { email: data.gmail.email, provider: 'gmail' } : null);
      setOutlookConnection(data.outlook?.connected ? { email: data.outlook.email, provider: 'outlook' } : null);

      if (statsRes.data?.stats) setEmailStats(statsRes.data.stats);
    } catch (error) {
      console.warn('Could not fetch email status:', error.message);
      setGmailConnection(null);
      setOutlookConnection(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchEmailStatus(); }, []);

  const handleDisconnect = async (provider) => {
    const label = provider === 'outlook' ? 'Outlook' : 'Gmail';
    if (!window.confirm(`Are you sure you want to disconnect ${label}? You will need to reconnect to send emails.`)) return;

    const setter = provider === 'outlook' ? setIsDisconnectingOutlook : setIsDisconnectingGmail;
    setter(true);
    try {
      await api.delete(`/api/email/disconnect?provider=${provider}`);
      if (provider === 'outlook') setOutlookConnection(null);
      else setGmailConnection(null);
    } catch (error) {
      console.error(`Failed to disconnect ${label}:`, error);
      alert(`Failed to disconnect ${label}. Please try again.`);
    } finally {
      setter(false);
    }
  };

  const handleConnect = async (provider) => {
    const setter = provider === 'outlook' ? setIsConnectingOutlook : setIsConnectingGmail;
    setter(true);
    try {
      const endpoint = provider === 'outlook' ? '/api/email/oauth/outlook/url' : '/api/email/oauth/google/url';
      const response = await api.get(endpoint, {
        params: { returnUrl: '/dashboard/settings?tab=integrations' },
      });
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No OAuth URL returned');
      }
    } catch (error) {
      const label = provider === 'outlook' ? 'Outlook' : 'Gmail';
      console.error(`Failed to initiate ${label} OAuth:`, error);
      alert(`Failed to connect ${label}. Please try again.`);
      setter(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#5a20ff]" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-xl flex items-center justify-center shadow-lg shadow-[#5a20ff]/20">
          <LinkIcon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-black font-serif">Integrations</h3>
          <p className="text-sm text-black/50">Connect your email and other services</p>
        </div>
      </div>

      <EmailProviderCard
        provider="gmail"
        label="Gmail"
        iconSrc="https://www.google.com/favicon.ico"
        description="Send emails directly from EQ using your Gmail account"
        connection={gmailConnection}
        emailStats={gmailConnection ? emailStats : null}
        onConnect={() => handleConnect('gmail')}
        onDisconnect={() => handleDisconnect('gmail')}
        isConnecting={isConnectingGmail}
        isDisconnecting={isDisconnectingGmail}
      />

      <EmailProviderCard
        provider="outlook"
        label="Outlook"
        iconSrc="https://outlook.live.com/favicon.ico"
        description="Send emails directly from EQ using your Outlook / Microsoft 365 account"
        connection={outlookConnection}
        emailStats={outlookConnection ? emailStats : null}
        onConnect={() => handleConnect('outlook')}
        onDisconnect={() => handleDisconnect('outlook')}
        isConnecting={isConnectingOutlook}
        isDisconnecting={isDisconnectingOutlook}
      />

      {/* Coming Soon */}
      <div className="bg-[#f8f8f6]/50 rounded-2xl border border-black/5 p-6">
        <h4 className="text-sm font-semibold text-black/40 mb-4 uppercase tracking-wider">Coming Soon</h4>
        <div className="space-y-3">
          {[
            { name: 'Salesforce', icon: '☁️', desc: 'Sync contacts and activities' },
            { name: 'HubSpot', icon: '🧡', desc: 'Log outreach to CRM' },
          ].map((item) => (
            <div key={item.name} className="flex items-center gap-3 p-3 bg-white/50 rounded-xl border border-black/5 opacity-60">
              <span className="text-2xl">{item.icon}</span>
              <div>
                <p className="font-medium text-black">{item.name}</p>
                <p className="text-xs text-black/50">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AccountTab({ profile, setProfile, user }) {
  const { subscription: realSubscription, orgPlanType, creditBalance, isPaidPlan, planInfo, hasAccess: orgHasAccess } = useSubscription();
  const [local, setLocal] = useState(profile);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    // Merge profile data from backend with user data from Auth0
    const mergedProfile = {
      firstName: profile.firstName || user?.given_name || "",
      lastName: profile.lastName || user?.family_name || "",
      email: profile.email || user?.email || "",
    };
    setLocal(mergedProfile);
    setIsChanged(false);
  }, [profile, user]);

  useEffect(() => {
    const hasChanges =
      local.firstName !== profile.firstName ||
      local.lastName !== profile.lastName ||
      local.email !== profile.email;
    setIsChanged(hasChanges);
  }, [local, profile]);

  const onSave = () => {
    // Non-functional: just log
    // eslint-disable-next-line no-console
    console.log("Save profile (mock):", local);
    setProfile(local);
    setIsChanged(false);
  };

  const onCancel = () => {
    setLocal(profile);
    setIsChanged(false);
  };

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-xl flex items-center justify-center shadow-lg shadow-[#5a20ff]/20">
          <User className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-black font-serif">
            Account Settings
          </h3>
          <p className="text-sm text-black/50">
            Manage your personal information and account preferences
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-black/70 mb-2 font-serif">
              First Name
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-black/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff] bg-[#f8f8f6] transition-colors"
              value={local.firstName || ""}
              onChange={(e) =>
                setLocal((s) => ({ ...s, firstName: e.target.value }))
              }
              placeholder={profile.firstName || "First name"}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-black/70 mb-2 font-serif">
              Last Name
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-black/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff] bg-[#f8f8f6] transition-colors"
              value={local.lastName || ""}
              onChange={(e) =>
                setLocal((s) => ({ ...s, lastName: e.target.value }))
              }
              placeholder={profile.lastName || "Last name"}
            />
          </div>
        </div>

        {/* Email Field */}
        <div>
          <label className="block text-sm font-medium text-black/70 mb-2 font-serif">
            Email Address
          </label>
          <input
            type="email"
            className="w-full px-4 py-3 border border-black/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff] bg-[#f8f8f6] transition-colors"
            value={local.email || ""}
            onChange={(e) => setLocal((s) => ({ ...s, email: e.target.value }))}
            placeholder={user?.email || profile.email || "email@example.com"}
          />
        </div>

        {/* Profile Summary */}
        <div className="bg-gradient-to-br from-[#5a20ff]/5 to-[#52dcb3]/5 rounded-2xl p-6 mt-8 border border-black/5">
          <h4 className="text-lg font-semibold text-black mb-4 font-serif">
            Profile Summary
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-black/50">Email verified:</span>
                <span
                  className={cn(
                    "text-sm font-medium",
                    user?.email_verified ? "text-[#52dcb3]" : "text-amber-500"
                  )}
                >
                  {user?.email_verified !== undefined
                    ? user.email_verified
                      ? "Verified"
                      : "Pending"
                    : "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-black/50">
                  Credits:
                </span>
                <span className="text-sm font-medium text-black">
                  {realSubscription?.unlimited
                    ? "Unlimited"
                    : isPaidPlan
                    ? creditBalance
                    : `${realSubscription?.usedToday || 0}/${realSubscription?.dailyLimit || 0} today`}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-black/50">Plan:</span>
                <span className="text-sm font-medium text-black">
                  {planInfo?.name || (orgPlanType !== "free"
                    ? orgPlanType.charAt(0).toUpperCase() + orgPlanType.slice(1)
                    : "Free Tier")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-black/50">Access status:</span>
                <span
                  className={cn(
                    "text-sm font-medium",
                    (orgHasAccess || realSubscription?.hasAccess)
                      ? "text-[#52dcb3]"
                      : "text-amber-500"
                  )}
                >
                  {(orgHasAccess || realSubscription?.hasAccess)
                    ? "Full Access"
                    : "Limited Access"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-black/50">
                  Credits remaining:
                </span>
                <span
                  className={cn(
                    "text-sm font-medium",
                    realSubscription?.unlimited || creditBalance > 0
                      ? "text-[#52dcb3]"
                      : "text-red-500"
                  )}
                >
                  {realSubscription?.unlimited
                    ? "Unlimited"
                    : isPaidPlan
                    ? creditBalance
                    : realSubscription?.remaining || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-6">
          <button
            onClick={onSave}
            disabled={!isChanged}
            className={cn(
              "px-6 py-2.5 rounded-full font-medium transition-all font-serif",
              isChanged
                ? "bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white hover:shadow-lg hover:shadow-[#5a20ff]/30 hover:scale-[1.02]"
                : "bg-black/10 text-black/30 cursor-not-allowed"
            )}
          >
            Save Changes
          </button>
          <button
            onClick={onCancel}
            disabled={!isChanged}
            className={cn(
              "px-6 py-2.5 rounded-full font-medium border transition-all font-serif",
              isChanged
                ? "border-black/20 text-black/70 hover:bg-black/5"
                : "border-black/10 text-black/30 cursor-not-allowed"
            )}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Plan tier ranking for upgrade/downgrade logic
const PLAN_RANK = { free: 0, starter: 1, individual: 1, growth: 2, team: 2, scale: 3, enterprise: 4 };

function PlanCardsSection({ orgPlanType, isFreePlan, upgradeLoading, onUpgrade, onManageBilling }) {
  const currentRank = PLAN_RANK[orgPlanType] ?? 0;

  const planButtonLabel = (planKey, planRank) => {
    if (currentRank === planRank) return null; // current plan — no button
    return planRank < currentRank ? "Downgrade" : "Upgrade";
  };

  const planButtonStyle = (action) => {
    if (action === "Downgrade") {
      return "w-full px-3 py-2 border border-black/15 text-black/60 rounded-xl font-semibold hover:bg-black/5 transition-all font-serif text-xs disabled:opacity-50 flex items-center justify-center gap-1.5";
    }
    return null; // upgrade buttons keep their individual colors
  };

  const plans = [
    {
      key: "starter",
      name: "Starter",
      rank: 1,
      subtitle: "The Sales Navigator Killer",
      price: 49,
      color: "#5a20ff",
      isCurrent: orgPlanType === "starter" || orgPlanType === "individual",
      borderCurrent: "border-2 border-[#5a20ff] ring-2 ring-[#5a20ff]/10",
      borderDefault: "border border-black/10",
      badgeColor: "text-[#5a20ff]",
      btnClass: "w-full px-3 py-2 bg-[#5a20ff] text-white rounded-xl font-semibold hover:bg-[#4a18d9] transition-all font-serif text-xs disabled:opacity-50 flex items-center justify-center gap-1.5",
      features: ["40 credits / month", "Full AI search access", "Basic AI outreach", "CRM integrations"],
    },
    {
      key: "growth",
      name: "Growth",
      rank: 2,
      subtitle: "The Core BD Engine",
      price: 149,
      color: "#52dcb3",
      isCurrent: orgPlanType === "growth" || orgPlanType === "team",
      borderCurrent: "border-2 border-[#52dcb3] ring-2 ring-[#52dcb3]/10",
      borderDefault: "border border-[#52dcb3]/30",
      badgeColor: "text-[#52dcb3]",
      popular: true,
      btnClass: "w-full px-3 py-2 bg-[#52dcb3] text-white rounded-xl font-semibold hover:bg-[#42c9a3] transition-all font-serif text-xs disabled:opacity-50 flex items-center justify-center gap-1.5",
      features: ["200 credits / month", "Everything in Starter", "Advanced AI outreach sequencing", "Priority email support"],
    },
    {
      key: "scale",
      name: "Scale",
      rank: 3,
      subtitle: "AI Transformation at Scale",
      price: 499,
      color: "#3b82f6",
      isCurrent: orgPlanType === "scale",
      borderCurrent: "border-2 border-blue-500 ring-2 ring-blue-500/10",
      borderDefault: "border border-black/10",
      badgeColor: "text-blue-600",
      btnClass: "w-full px-3 py-2 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all font-serif text-xs disabled:opacity-50 flex items-center justify-center gap-1.5",
      features: ["750 credits / month", "Everything in Growth", "EQenrich bulk uploads", "Shared team credit pools", "API access"],
    },
  ];

  return (
    <div className="bg-gradient-to-br from-[#5a20ff]/5 to-[#52dcb3]/5 border border-[#5a20ff]/10 rounded-2xl p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-1">
        <Sparkles className="w-5 h-5 text-[#5a20ff]" />
        <h3 className="text-base lg:text-lg font-semibold text-black font-serif">
          {isFreePlan ? "Search for Free, Pay for Reveals" : "Available Plans"}
        </h3>
      </div>
      {isFreePlan && (
        <p className="text-xs text-black/40 mb-4 ml-8">
          Create an account and search for free. Only pay when you reveal contacts or use AI outreach.
        </p>
      )}
      {!isFreePlan && (
        <p className="text-xs text-black/40 mb-4 ml-8">
          Upgrade, downgrade, or compare plans. Changes take effect at the next billing cycle.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {plans.map((plan) => {
          const action = planButtonLabel(plan.key, plan.rank);
          const isDowngrade = action === "Downgrade";
          return (
            <div
              key={plan.key}
              className={cn(
                "bg-white rounded-2xl p-4 flex flex-col relative",
                plan.isCurrent ? plan.borderCurrent : plan.borderDefault
              )}
            >
              {plan.isCurrent && (
                <span className={cn("text-[10px] font-bold uppercase tracking-wide mb-2", plan.badgeColor)}>
                  Current Plan
                </span>
              )}
              {plan.popular && !plan.isCurrent && (
                <span className="absolute -top-2.5 right-3 bg-[#52dcb3] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">
                  Most Popular
                </span>
              )}

              <h4 className="text-base font-bold text-black font-serif">{plan.name}</h4>
              <p className="text-xs text-black/40 mb-2">{plan.subtitle}</p>
              <span className="text-xl font-bold" style={{ color: plan.color }}>
                ${plan.price}<span className="text-xs font-normal text-black/40">/mo</span>
              </span>

              <ul className="space-y-1.5 text-xs text-black/60 my-3 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-[#52dcb3] flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              {action && (
                <button
                  onClick={() => isDowngrade ? onManageBilling() : onUpgrade(plan.key)}
                  disabled={upgradeLoading !== null}
                  className={isDowngrade ? planButtonStyle("Downgrade") : plan.btnClass}
                >
                  {upgradeLoading === plan.key ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    `${action} to ${plan.name}`
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Enterprise footnote */}
      {orgPlanType === "enterprise" ? (
        <div className="mt-4 pt-3 border-t border-black/5 flex items-center justify-center gap-2 text-xs text-black/50">
          <Crown className="w-3.5 h-3.5 text-yellow-500" />
          <span>You're on a custom Enterprise plan. Contact your account manager to make changes.</span>
        </div>
      ) : (
        <div className="mt-4 pt-3 border-t border-black/5 flex items-center justify-center gap-2 text-xs text-black/40">
          <span>Need more?</span>
          <a
            href="mailto:hello@eq.app?subject=EQ Scout Enterprise Inquiry"
            className="inline-flex items-center gap-1 text-[#5a20ff] hover:text-[#4a18d9] font-medium transition-colors"
          >
            <Mail className="w-3 h-3" />
            Contact us for custom Enterprise pricing
          </a>
        </div>
      )}
    </div>
  );
}

function WorkspaceTab() {
  const {
    org,
    orgPlanType,
    planInfo,
    creditBalance,
    monthlyAllowance,
    monthlySearchLimit,
    isFreePlan,
    isPaidPlan,
    allowsMembers,
    userRole,
    memberCount,
    maxSeats,
  } = useSubscription();

  // Billing / credits state
  const [purchaseLoading, setPurchaseLoading] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(null);
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);
  const [showPlanCards, setShowPlanCards] = useState(false);

  // Team state
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [resendSuccess, setResendSuccess] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [changingRoleId, setChangingRoleId] = useState(null);

  // Workspace rename state
  const [editingName, setEditingName] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const isOwner = userRole === "owner";
  const isOwnerOrAdmin = userRole === "owner" || userRole === "admin";

  const handleSaveName = async () => {
    const trimmed = newOrgName.trim();
    if (!trimmed || trimmed === org.name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      await updateOrgSettings({ name: trimmed });
      setEditingName(false);
      window.location.reload();
    } catch (err) {
      console.error("Failed to rename workspace:", err);
    } finally {
      setSavingName(false);
    }
  };

  useEffect(() => {
    loadMembers();
    loadUsage();
  }, []);

  const loadMembers = async () => {
    setLoadingMembers(true);
    try {
      const [memberData, inviteData] = await Promise.all([
        getOrgMembers().catch(() => []),
        getOrgInvitations().catch(() => []),
      ]);
      setMembers(memberData || []);
      setPendingInvites(inviteData || []);
    } catch (_err) {
      /* fail-open */
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadUsage = async () => {
    setLoadingUsage(true);
    try {
      const data = await getOrgUsage();
      setUsage(data);
    } catch (_err) {
      /* fail-open */
    } finally {
      setLoadingUsage(false);
    }
  };

  const onManageBilling = async () => {
    try {
      const response = await api.post("/api/billing/customer-portal");
      if (response.data?.url) window.open(response.data.url, "_blank");
    } catch (error) {
      console.error("Failed to open Customer Portal:", error);
    }
  };

  const handleBuyCredits = async (quantity) => {
    setPurchaseLoading(quantity);
    try {
      await startCreditPurchase(quantity);
    } catch (error) {
      console.error("Failed to purchase credits:", error);
      alert("Failed to start checkout. Please try again.");
    } finally {
      setPurchaseLoading(null);
    }
  };

  const handleUpgrade = async (planKey) => {
    setUpgradeLoading(planKey);
    try {
      await startCheckout({ planKey });
    } catch (error) {
      console.error("Failed to start checkout:", error);
      alert("Failed to start checkout. Please try again.");
    } finally {
      setUpgradeLoading(null);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    setInviteError(null);
    setInviteResult(null);
    try {
      const data = await inviteMember(inviteEmail.trim(), inviteRole);
      setInviteResult(data);
      setInviteEmail("");
      setInviteRole("member");
      loadMembers();
    } catch (err) {
      setInviteError(err?.response?.data?.error || err.message || "Failed to send invite");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm("Remove this member from the organization?")) return;
    setRemovingId(userId);
    try {
      await removeOrgMember(userId);
      loadMembers();
    } catch (_err) {
      alert("Failed to remove member. Please try again.");
    } finally {
      setRemovingId(null);
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleRoleChange = async (userId, newRole) => {
    setChangingRoleId(userId);
    try {
      await updateOrgMemberRole(userId, newRole);
      loadMembers();
    } catch (_err) {
      alert("Failed to update role. Please try again.");
    } finally {
      setChangingRoleId(null);
    }
  };

  const roleIcon = (role) => {
    if (role === "owner") return <Crown className="w-4 h-4 text-yellow-500" />;
    if (role === "admin") return <Shield className="w-4 h-4 text-[#5a20ff]" />;
    return <User className="w-4 h-4 text-black/40" />;
  };

  const planBadgeColor = {
    free: "bg-black/5 text-black/60",
    starter: "bg-[#5a20ff]/10 text-[#5a20ff]",
    individual: "bg-[#5a20ff]/10 text-[#5a20ff]",
    growth: "bg-[#52dcb3]/10 text-[#52dcb3]",
    team: "bg-[#52dcb3]/10 text-[#52dcb3]",
    scale: "bg-blue-100 text-blue-700",
    trial: "bg-amber-100 text-amber-700",
    enterprise: "bg-yellow-100 text-yellow-800",
  };

  const CREDIT_MIN = 25;
  const CREDIT_MAX = 5000;
  const CREDIT_TICK_LABELS = [25, 100, 500, 1000, 2500, 5000];
  const [selectedCredits, setSelectedCredits] = useState(100);

  const getCreditPrice = (qty) => {
    if (qty <= 99) return 1.50;
    if (qty <= 499) return 1.25;
    if (qty <= 999) return 1.00;
    if (qty <= 2499) return 0.80;
    return 0.65;
  };
  const selectedPricePerCredit = getCreditPrice(selectedCredits);
  const selectedTotal = selectedCredits * selectedPricePerCredit;

  const handleCreditInput = (val) => {
    const n = parseInt(val, 10);
    if (isNaN(n)) return;
    setSelectedCredits(Math.max(CREDIT_MIN, Math.min(CREDIT_MAX, n)));
  };

  if (!org) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#5a20ff] mb-3" />
        <p className="text-black/50">Loading organization...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* ── Workspace Header ── */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-xl flex items-center justify-center shadow-lg shadow-[#5a20ff]/20">
          <Users className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          {editingName ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveName();
                  if (e.key === "Escape") setEditingName(false);
                }}
                className="text-xl font-semibold text-black font-serif bg-[#f8f8f6] border border-[#5a20ff]/20 rounded-lg px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 w-full max-w-xs"
                placeholder="Workspace name"
              />
              <button
                onClick={handleSaveName}
                disabled={savingName}
                className="p-1.5 bg-[#5a20ff] text-white rounded-lg hover:bg-[#5a20ff]/90 transition-colors disabled:opacity-50"
                title="Save"
              >
                {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h3 className="text-xl font-semibold text-black font-serif">
                {org.name || "Your Workspace"}
              </h3>
              {isOwnerOrAdmin && (
                <button
                  onClick={() => { setNewOrgName(org.name || ""); setEditingName(true); }}
                  className="p-1 text-black/20 hover:text-[#5a20ff] transition-colors opacity-0 group-hover:opacity-100"
                  title="Rename workspace"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
          <p className="text-sm text-black/50">
            {members.length} member{members.length !== 1 ? "s" : ""}
          </p>
        </div>
        <span
          className={cn(
            "inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide",
            planBadgeColor[orgPlanType] || planBadgeColor.free
          )}
        >
          {planInfo?.name || "Free"}
        </span>
      </div>

      {/* ── Plan + Credits summary row ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Credit Balance */}
        <div className="bg-gradient-to-br from-[#5a20ff]/5 to-[#52dcb3]/5 border border-[#5a20ff]/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-[#5a20ff]" />
            <span className="text-sm font-semibold text-black font-serif">Credits</span>
          </div>
          <div className="text-4xl font-bold text-[#5a20ff]">{creditBalance}</div>
          <div className="text-xs text-black/50 mt-1">
            {monthlyAllowance > 0
              ? `${monthlyAllowance}/mo included with plan`
              : "Top up anytime"}
          </div>
          <button
            onClick={() => setShowCreditPurchase(!showCreditPurchase)}
            className="mt-3 px-4 py-2 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#5a20ff]/30 transition-all hover:scale-[1.02] font-serif text-xs flex items-center gap-2"
          >
            <Zap className="w-3.5 h-3.5" />
            Buy Credits
          </button>
        </div>

        {/* Plan Info */}
        <div className="bg-white border border-black/5 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-4 h-4 text-[#5a20ff]" />
            <span className="text-sm font-semibold text-black font-serif">Plan</span>
          </div>
          <div className="text-lg font-bold text-black">{planInfo?.name || "Free"}</div>
          <div className="text-xs text-black/40 mt-1">
            {monthlySearchLimit > 0
              ? `${monthlySearchLimit.toLocaleString()} searches/mo`
              : "Unlimited searches"}
          </div>
          <button
            onClick={() => setShowPlanCards(!showPlanCards)}
            className="mt-3 px-4 py-2 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#5a20ff]/30 transition-all hover:scale-[1.02] font-serif text-xs flex items-center gap-2"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Manage Plan
          </button>
          <button
            onClick={onManageBilling}
            className="mt-2 text-[11px] text-black/40 hover:text-[#5a20ff] transition-colors flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" />
            Billing history &amp; invoices
          </button>
        </div>
      </div>

      {/* ── Credit Purchase (sliding scale) ── */}
      {showCreditPurchase && (
        <div className="bg-white border border-black/5 rounded-2xl p-4 lg:p-6 shadow-sm">
          <h3 className="text-base lg:text-lg font-semibold text-black font-serif mb-1">
            Purchase Credits
          </h3>
          <p className="text-xs text-black/40 mb-5">
            Slide to choose &mdash; buy more, pay less per credit
          </p>

          {/* Slider + input */}
          <div className="mb-6">
            <div className="flex items-center gap-4 mb-3">
              <input
                type="range"
                min={CREDIT_MIN}
                max={CREDIT_MAX}
                step={25}
                value={selectedCredits}
                onChange={(e) => setSelectedCredits(Number(e.target.value))}
                className="flex-1 h-2 bg-gradient-to-r from-[#5a20ff]/20 to-[#52dcb3]/40 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#5a20ff] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-[#5a20ff]/30 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:active:cursor-grabbing"
              />
              <input
                type="number"
                min={CREDIT_MIN}
                max={CREDIT_MAX}
                step={25}
                value={selectedCredits}
                onChange={(e) => handleCreditInput(e.target.value)}
                className="w-20 text-center border border-black/10 rounded-lg px-2 py-1.5 text-sm font-bold text-[#5a20ff] focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff]"
              />
            </div>
            <div className="flex justify-between px-0.5">
              {CREDIT_TICK_LABELS.map((s) => (
                <button
                  key={s}
                  onClick={() => setSelectedCredits(s)}
                  className={`text-[9px] font-medium transition-colors ${selectedCredits === s ? "text-[#5a20ff] font-bold" : "text-black/30 hover:text-black/50"}`}
                >
                  {s >= 1000 ? `${s / 1000}k` : s}
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-br from-[#5a20ff]/5 to-[#52dcb3]/5 border border-[#5a20ff]/10 rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-5">
            <div className="flex-1 text-center sm:text-left">
              <div className="text-4xl font-bold text-[#5a20ff]">{selectedCredits.toLocaleString()}</div>
              <div className="text-xs text-black/50 mt-0.5">credits</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-black">${selectedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="text-xs text-black/40">${selectedPricePerCredit.toFixed(2)} per credit</div>
              {selectedCredits >= 2500 && (
                <span className="inline-block mt-1 bg-[#52dcb3]/15 text-[#52dcb3] text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">Best Value</span>
              )}
            </div>
            <button
              onClick={() => handleBuyCredits(selectedCredits)}
              disabled={purchaseLoading !== null}
              className="px-6 py-3 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#5a20ff]/30 transition-all hover:scale-[1.02] font-serif text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {purchaseLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  Buy {selectedCredits.toLocaleString()} Credits
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Plan Cards (toggle, same pattern as Buy Credits) ── */}
      {showPlanCards && (
        <PlanCardsSection
          orgPlanType={orgPlanType}
          isFreePlan={isFreePlan}
          upgradeLoading={upgradeLoading}
          onUpgrade={handleUpgrade}
          onManageBilling={onManageBilling}
        />
      )}

      {/* ── Members ── */}
      <div className="bg-white border border-black/5 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 lg:p-5 border-b border-black/5 flex items-center justify-between">
          <h4 className="font-semibold text-black font-serif">Members</h4>
          {maxSeats > 0 && (
            <span className="text-xs text-black/40">{members.length} / {maxSeats} seats</span>
          )}
        </div>
        {loadingMembers ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-[#5a20ff]" />
          </div>
        ) : members.length === 0 ? (
          <div className="p-6 text-center text-black/40 text-sm">No members yet.</div>
        ) : (
          <div className="divide-y divide-black/5">
            {members.map((m) => {
              const displayName = m.name || m.email || m.user_id?.split("|").pop()?.slice(0, 8) || "Member";
              const subtitle = m.name && m.email ? m.email : (!m.name && !m.email ? "Pending profile" : null);
              return (
              <div key={m.user_id || m.userId || m.email} className="flex items-center gap-4 px-4 lg:px-5 py-3 hover:bg-[#f8f8f6]/50 transition-colors">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#5a20ff]/20 to-[#52dcb3]/20 flex items-center justify-center text-sm font-bold text-[#5a20ff]">
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-black text-sm truncate">{displayName}</div>
                  {subtitle && <div className="text-xs text-black/40 truncate">{subtitle}</div>}
                </div>
                {isOwner && m.role !== "owner" ? (
                  <div className="relative">
                    <select
                      value={m.role}
                      onChange={(e) => handleRoleChange(m.user_id || m.userId, e.target.value)}
                      disabled={changingRoleId === (m.user_id || m.userId)}
                      className="appearance-none bg-transparent border border-black/10 rounded-lg px-2.5 py-1 pr-7 text-xs text-black/60 cursor-pointer hover:border-[#5a20ff]/30 focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff] transition-colors disabled:opacity-50"
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-1.5 flex items-center">
                      {changingRoleId === (m.user_id || m.userId)
                        ? <Loader2 className="w-3 h-3 animate-spin text-[#5a20ff]" />
                        : <Shield className="w-3 h-3 text-black/30" />}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-xs text-black/50">
                    {roleIcon(m.role)}
                    <span className="capitalize">{m.role}</span>
                  </div>
                )}
                {(m.joined_at || m.joinedAt) && (
                  <div className="hidden sm:block text-xs text-black/30">
                    Joined {new Date(m.joined_at || m.joinedAt).toLocaleDateString()}
                  </div>
                )}
                {isOwnerOrAdmin && m.role !== "owner" && (
                  <button
                    onClick={() => handleRemove(m.user_id || m.userId)}
                    disabled={removingId === (m.user_id || m.userId)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Remove member"
                  >
                    {removingId === (m.user_id || m.userId) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invite (team plan + owner/admin only) */}
      {allowsMembers && isOwnerOrAdmin && (
        <div className="bg-white border border-black/5 rounded-2xl p-4 lg:p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <UserPlus className="w-5 h-5 text-[#5a20ff]" />
            <h4 className="font-semibold text-black font-serif">Invite Member</h4>
          </div>
          <div className="flex gap-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInvite()}
              placeholder="colleague@company.com"
              className="flex-1 px-4 py-2.5 border border-black/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff] bg-[#f8f8f6] text-sm transition-colors"
            />
            {isOwner && (
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="px-3 py-2.5 border border-black/10 rounded-xl bg-[#f8f8f6] text-sm text-black/70 focus:outline-none focus:ring-2 focus:ring-[#5a20ff]/20 focus:border-[#5a20ff] transition-colors"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            )}
            <button
              onClick={handleInvite}
              disabled={inviteLoading || !inviteEmail.trim()}
              className="px-5 py-2.5 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#5a20ff]/30 transition-all hover:scale-[1.02] font-serif text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {inviteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" />Send Invite</>}
            </button>
          </div>
          {inviteError && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{inviteError}
            </div>
          )}
          {inviteResult && (
            <div className="mt-3 bg-[#52dcb3]/10 border border-[#52dcb3]/20 px-3 py-2.5 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-[#52dcb3] font-medium mb-1">
                <Check className="w-4 h-4" />Invitation sent to {inviteResult.invitation?.email || "member"}!
              </div>
              {(inviteResult.invitation?.inviteLink || inviteResult.inviteUrl) && (
                <div className="flex items-center gap-2 mt-2">
                  <input readOnly value={inviteResult.invitation?.inviteLink || inviteResult.inviteUrl} className="flex-1 text-xs bg-white border border-black/10 rounded-lg px-3 py-1.5 text-black/60" />
                  <button onClick={() => handleCopyLink(inviteResult.invitation?.inviteLink || inviteResult.inviteUrl)} className="p-1.5 text-[#5a20ff] hover:bg-[#5a20ff]/10 rounded-lg transition-colors" title="Copy invite link">
                    {copiedLink ? <Check className="w-4 h-4 text-[#52dcb3]" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Nudge for non-team plans */}
      {!allowsMembers && (
        <div className="bg-[#f8f8f6] border border-black/5 rounded-2xl p-5 text-center">
          <Users className="w-8 h-8 text-black/20 mx-auto mb-2" />
          <p className="text-sm text-black/50 mb-1">
            Invite &amp; manage team members on the Growth or Scale plan.
          </p>
          <button
            onClick={() => setShowPlanCards(true)}
            className="mt-3 px-5 py-2.5 bg-[#52dcb3] text-white rounded-xl font-semibold hover:bg-[#42c9a3] transition-all font-serif text-sm inline-flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            View Plans
          </button>
        </div>
      )}

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div className="bg-white border border-black/5 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 lg:p-5 border-b border-black/5">
            <h4 className="font-semibold text-black font-serif">
              Pending Invitations ({pendingInvites.length})
            </h4>
          </div>
          <div className="divide-y divide-black/5">
            {pendingInvites.map((inv) => (
              <div key={inv.id || inv.email} className="flex items-center gap-4 px-4 lg:px-5 py-3">
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-black truncate">{inv.email}</div>
                  <div className="text-xs text-black/40">
                    <span className="capitalize">{inv.role || "member"}</span> &middot;{" "}
                    Invited {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ""} &middot;{" "}
                    <span className="text-amber-600 font-medium">Pending</span>
                    {inv.expires_at && (
                      <> &middot; Expires {new Date(inv.expires_at).toLocaleDateString()}</>
                    )}
                  </div>
                </div>
                {isOwnerOrAdmin && (
                  <div className="flex items-center gap-1">
                    {resendSuccess === inv.id ? (
                      <span className="text-xs text-green-600 font-medium px-2">Sent!</span>
                    ) : (
                      <button
                        onClick={async () => {
                          setResendingId(inv.id);
                          setResendSuccess(null);
                          try {
                            await resendOrgInvitation(inv.id);
                            setResendSuccess(inv.id);
                            setTimeout(() => setResendSuccess(null), 3000);
                          } catch (_err) {
                            alert("Failed to resend invitation.");
                          } finally {
                            setResendingId(null);
                          }
                        }}
                        disabled={resendingId === inv.id}
                        className="p-1.5 text-[#5a20ff] hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Resend invitation email"
                      >
                        {resendingId === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        setRemovingId(inv.id);
                        try {
                          await revokeOrgInvitation(inv.id);
                          loadMembers();
                        } catch (_err) {
                          alert("Failed to revoke invitation.");
                        } finally {
                          setRemovingId(null);
                        }
                      }}
                      disabled={removingId === inv.id}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Revoke invitation"
                    >
                      {removingId === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Usage by Member ── */}
      <div className="bg-white border border-black/5 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-4 lg:p-5 border-b border-black/5">
          <h4 className="font-semibold text-black font-serif">Usage This Month</h4>
        </div>
        {loadingUsage ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-[#5a20ff]" />
          </div>
        ) : !usage?.members || usage.members.length === 0 ? (
          <div className="p-6 text-center text-black/40 text-sm">No usage data yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-black/40 uppercase tracking-wider">
                  <th className="px-4 lg:px-5 py-3 font-medium">Member</th>
                  <th className="px-4 lg:px-5 py-3 font-medium">Searches</th>
                  <th className="px-4 lg:px-5 py-3 font-medium">Reveals</th>
                  <th className="px-4 lg:px-5 py-3 font-medium">Outreach</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5">
                {usage.members.map((u) => (
                  <tr key={u.userId || u.email} className="hover:bg-[#f8f8f6]/50">
                    <td className="px-4 lg:px-5 py-3 font-medium text-black">{u.name || u.email}</td>
                    <td className="px-4 lg:px-5 py-3 text-black/60">{u.searches ?? 0}</td>
                    <td className="px-4 lg:px-5 py-3 text-black/60">{u.reveals ?? 0}</td>
                    <td className="px-4 lg:px-5 py-3 text-black/60">{u.outreach ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
